import { createStyles } from "@mantine/core";
import { QR, BTN_RESET } from "./theme";

const useDetailStyles = createStyles(() => ({
  pagerBar: { display: "flex", justifyContent: "center", marginBottom: 4 },
  pager: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 999,
    padding: 5,
    boxShadow: QR.shadowCard,
  },
  pagerArrow: {
    ...BTN_RESET,
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: QR.ink2,
    background: "transparent",
    "&:hover:not(:disabled)": { background: QR.surfaceAlt },
    "&:disabled": { color: QR.muted2, opacity: 0.45, cursor: "default" },
  },
  pagerMid: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 5,
    padding: "2px 22px",
    minWidth: 190,
  },
  pagerCount: { fontSize: 13.5, fontWeight: 600, color: QR.muted, lineHeight: 1, fontVariantNumeric: "tabular-nums" },
  pagerDots: { display: "flex", gap: 4, alignItems: "center" },
  pagerDot: { width: 5, height: 5, borderRadius: 999, background: QR.line, transition: "all .2s ease" },
  pagerDotDone: { background: QR.accentRing },
  pagerDotOn: { width: 16, background: QR.accent },

  // The whole review document renders as ONE continuous card (report-wrap in
  // the prototype) — toolbar/meta/Section A/B/C/Completion are segments
  // inside it, separated by a hairline divider, not separate bordered cards.
  reportWrap: {
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 12,
    boxShadow: QR.shadowCard,
    overflow: "hidden",
  },
  reportToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 28px",
    borderBottom: `1px solid ${QR.line}`,
    background: QR.surfaceSoft,
    flexWrap: "wrap",
  },
  toolbarTitle: { display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 700, color: QR.ink, letterSpacing: "-0.01em", fontFamily: "Inter" },
  toolbarActions: { display: "flex", gap: 10 },
  previewBtn: {
    ...BTN_RESET,
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: `1px solid ${QR.line}`,
    background: QR.surface,
    fontWeight: 600,
    fontSize: 13.5,
    color: QR.ink2,
    fontFamily: "Inter",
    "&:hover": { background: QR.surfaceSoft },
  },
  saveBtnInner: { display: "flex", alignItems: "center", gap: 8 },
  lockedBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    background: QR.dangerSoft,
    borderTop: "1px solid #FECACA",
    fontSize: 12.5,
    color: QR.dangerDeep,
    fontWeight: 600,
  },

  card: {
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 12,
    boxShadow: QR.shadowCard,
    overflow: "hidden",
  },
  // A segment inside reportWrap — no border/shadow of its own, and a hairline
  // rule above it (except the first) instead of a gap between separate cards.
  cardSegment: {
    position: "relative",
    "&:not(:first-of-type)::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 28,
      right: 28,
      height: 1,
      background: QR.line,
    },
  },
  // The meta-grid segment sits right after the report-toolbar (which isn't a
  // ".card" in the prototype and already has its own border-bottom) — it's
  // the first real card in the stack, so it must not get the divider rule
  // above even though it's not the first cardSegment child.
  cardSegmentNoTop: { "&::before": { display: "none" } },
  cardHead: {
    padding: "14px 20px",
    borderBottom: `1px solid ${QR.lineSoft}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardHeadInWrap: { paddingLeft: 28, paddingRight: 28 },
  cardHeadTitle: { fontSize: 15, fontWeight: 700, fontFamily: "Inter" },
  cardHeadHint: { fontSize: 12.5, color: QR.muted, marginTop: 2 },
  cardBody: { padding: 20, display: "flex", flexDirection: "column", gap: 18 },
  cardBodyInWrap: { paddingLeft: 28, paddingRight: 28 },

  metaGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 },
  metaLabel: { fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".03em", color: QR.muted, marginBottom: 4 },
  metaValue: { fontSize: 14.5, fontWeight: 600, color: QR.ink, fontFamily: "Inter" },

  outcome: {
    border: `1px solid ${QR.line}`,
    borderRadius: 10,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    background: QR.surfaceSoft,
  },
  outcomeTop: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" },
  // Section B's Risk type / Status pair (.form-grid) — a real 2-col grid,
  // not a wrapping flex row.
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  sigImage: { maxHeight: 48, maxWidth: "100%", objectFit: "contain" },
  outcomeTitle: { fontSize: 14, fontWeight: 700, color: QR.ink, fontFamily: "Inter" },
  outcomeMeta: { fontSize: 12, color: QR.muted, marginTop: 3 },
  provTag: {
    fontSize: 12,
    color: QR.muted,
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 6,
    padding: "4px 10px",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  fldLabel: { fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", color: QR.muted, letterSpacing: ".03em" },
  // "Report details" heading above the manual report's 2-column header grid.
  reportDetailsLabel: { marginBottom: 6 },
  ta: {
    width: "100%",
    minHeight: 78,
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: 10,
    fontSize: 13.5,
    fontFamily: "Inter",
    resize: "vertical",
    background: QR.surface,
  },
  inlineToggle: { display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 600, color: QR.ink2 },

  supportGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  supportItem: {
    border: `1px solid ${QR.line}`,
    borderRadius: 10,
    padding: "11px 14px",
    background: QR.surface,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  supportItemChecked: { background: QR.accentSoft, borderColor: QR.accentRing },
  supportRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    userSelect: "none",
    fontSize: 13.5,
    fontWeight: 600,
    fontFamily: "Inter",
  },
  cbx: {
    width: 18,
    height: 18,
    border: `1.5px solid ${QR.muted2}`,
    borderRadius: 5,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: QR.surface,
  },
  cbxOn: {
    background: QR.purple,
    borderColor: QR.purple,
    "&::after": {
      content: '""',
      width: 9,
      height: 5,
      borderLeft: "2px solid #fff",
      borderBottom: "2px solid #fff",
      transform: "rotate(-45deg) translate(0,-1px)",
    },
  },
  // The support item's own textarea (.support-input) — smaller/tighter than
  // the general .ta used everywhere else in this page.
  supportInputTa: {
    display: "block",
    width: "100%",
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    lineHeight: 1.5,
    resize: "vertical",
    minHeight: 54,
    background: QR.surface,
    fontFamily: "inherit",
    color: QR.ink,
    "&:focus": { outline: "none", borderColor: QR.accent },
  },

  qBlock: { display: "flex", flexDirection: "column", gap: 10, paddingBottom: 16, borderBottom: `1px solid ${QR.lineSoft}`, "&:last-child": { borderBottom: "none", paddingBottom: 0 } },
  qText: { fontSize: 13.5, fontWeight: 600, color: QR.ink, fontFamily: "Inter" },

  sigGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 },
  sigCell: { borderBottom: `1px solid ${QR.line}`, paddingBottom: 8 },
  sigVal: { fontSize: 14.5, fontWeight: 600, color: QR.ink, minHeight: 22, fontFamily: "Inter" },
  sigScript: {
    fontFamily: '"Segoe Script","Snell Roundhand","Brush Script MT",cursive',
    fontStyle: "italic",
    fontWeight: 400,
    fontSize: 24,
    lineHeight: 1.1,
    color: QR.ink,
  },
  sigLbl: { fontSize: 11.5, fontWeight: 600, color: QR.muted, marginTop: 6, textTransform: "uppercase", letterSpacing: ".03em" },
  // Review banner — selections the engine could not evidence. Deliberately
  // reads as a to-do list, not an error: acknowledging these at signing is the
  // normal path.
  flagBanner: {
    background: QR.accentSoft,
    border: `1px solid ${QR.accentRing}`,
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 16,
  },
  flagBannerTitle: { fontSize: 14.5, fontWeight: 700, color: QR.ink },
  flagBannerLead: { fontSize: 12.5, color: QR.muted, marginTop: 4, marginBottom: 10 },
  flagItem: {
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: "9px 11px",
    marginTop: 7,
    cursor: "pointer",
    "&:hover": { borderColor: QR.accent },
  },
  flagItemSection: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".03em",
    color: QR.muted,
  },
  flagItemDetail: { fontSize: 13, color: QR.ink2, marginTop: 2 },
  signDocBtn: {
    ...BTN_RESET,
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 36,
    padding: "0 14px",
    borderRadius: 8,
    background: QR.accent,
    border: `1px solid ${QR.accent}`,
    color: QR.ink,
    fontSize: 13,
    fontWeight: 700,
    textAlign: "center",
    "&:hover": { background: QR.accentHover },
  },
  // ─── Manual (hand-entered) report affordances ─────────────────────────────
  // A manual report has no plan behind it, so the fields an AI draft renders as
  // read-only plan data (outcome/risk titles, individual, facility, ISP
  // revision dates) are typed in here instead.
  inp: {
    width: "100%",
    height: 38,
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: "0 12px",
    background: QR.surface,
    fontSize: 13.5,
    color: QR.ink,
    fontFamily: "Inter",
    "&:focus": { outline: "none", borderColor: QR.accent },
    "&:disabled": { background: QR.surfaceSoft, color: QR.ink2, cursor: "default" },
  },
  inpTitle: { fontWeight: 600 },
  // Dashed "Add outcome / Add risk / Add evidence" button.
  addBtn: {
    ...BTN_RESET,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    border: `1.5px dashed ${QR.line}`,
    borderRadius: 10,
    padding: 11,
    fontSize: 13,
    fontWeight: 600,
    color: QR.muted,
    background: QR.surface,
    fontFamily: "Inter",
    "&:hover": { borderColor: QR.purpleRing, color: QR.purple, background: QR.purpleSoft },
  },
  rowRemoveBtn: {
    ...BTN_RESET,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    borderRadius: 8,
    color: QR.muted,
    flexShrink: 0,
    "&:hover": { background: QR.dangerSoft, color: QR.dangerDeep },
  },
  infoBanner: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 13,
    border: `1px solid ${QR.purpleRing}`,
    background: QR.purpleSoft,
    color: QR.purpleDeep,
    fontFamily: "Inter",
  },
  eviStrip: { display: "flex", gap: 10, flexWrap: "wrap" },
  eviItem: {
    width: 104,
    border: `1px solid ${QR.line}`,
    borderRadius: 10,
    overflow: "hidden",
    background: QR.surface,
    position: "relative",
  },
  eviThumb: {
    height: 68,
    background: QR.surfaceAlt,
    display: "grid",
    placeItems: "center",
    color: QR.muted2,
  },
  eviThumbDoc: { background: "#EFF4FF", color: QR.info },
  eviThumbImg: { width: "100%", height: "100%", objectFit: "cover" },
  eviCap: {
    fontSize: 10.5,
    color: QR.ink2,
    padding: "6px 8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontFamily: "Inter",
  },
  eviRemove: {
    ...BTN_RESET,
    position: "absolute",
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    borderRadius: 6,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,.9)",
    border: `1px solid ${QR.line}`,
    color: QR.muted,
    "&:hover": { background: QR.dangerSoft, color: QR.dangerDeep, borderColor: "#FECACA" },
  },
  eviAdd: {
    ...BTN_RESET,
    width: 104,
    minHeight: 98,
    border: `1.5px dashed ${QR.line}`,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    color: QR.muted,
    background: QR.surfaceSoft,
    "&:hover": { borderColor: QR.purpleRing, color: QR.purple, background: QR.purpleSoft },
  },
}));

export default useDetailStyles;
